<template>
    <div class="blacklist-container">
        <!-- 顶部标题和操作区域 -->
        <el-card shadow="never" class="header-card">
            <div class="header-content">
                <div class="header-actions">
                    <el-input v-model="searchQuery" placeholder="搜索UID/昵称" clearable style="width: 240px"
                        @keyup.enter="handleSearch" @clear="handleSearch">
                        <template #append>
                            <el-button :icon="Search" @click="handleSearch" />
                        </template>
                    </el-input>
                    <el-button type="primary" :icon="Refresh" @click="fetchBlacklist">刷新</el-button>
                </div>
            </div>
        </el-card>

        <!-- 数据表格区域 -->
        <el-card shadow="never" class="table-card">
            <el-table :data="filteredTableData" border stripe style="width: 100%" v-loading="loading"
                :header-cell-style="{ background: '#f5f7fa', color: '#606266' }">
                <el-table-column type="index" label="序号" width="60" align="center" />
                <el-table-column prop="id" label="ID" width="120" align="center" />
                <el-table-column prop="uid" label="B站UID/QQ" width="150" align="center" />
                <el-table-column prop="name" label="昵称" width="120" show-overflow-tooltip />
                <el-table-column prop="violation" label="违规行为" min-width="180" show-overflow-tooltip />
                <el-table-column prop="handle" label="处理方式" min-width="150" show-overflow-tooltip />
                <el-table-column prop="handleman" label="处理人" width="120" align="center" />
                <el-table-column prop="platform" label="所属平台" width="100" align="center">
                    <template #default="scope">
                        <el-tag :type="scope.row.platform === 'B站' ? 'danger' : 'primary'" effect="plain">
                            {{ scope.row.platform }}
                        </el-tag>
                    </template>
                </el-table-column>
                <el-table-column label="图片材料" width="150" align="center">
                    <template #default="scope">
                        <div class="image-container">
                            <el-image v-if="scope.row.materials" :src="getImageUrl(scope.row.materials)" fit="cover"
                                class="table-image" :preview-src-list="[getImageUrl(scope.row.materials)]"
                                :hide-on-click-modal="true">
                                <template #error>
                                    <div class="image-error">
                                        <el-icon>
                                            <Picture />
                                        </el-icon>
                                    </div>
                                </template>
                            </el-image>
                            <div v-else class="no-image">
                                <el-icon>
                                    <Picture />
                                </el-icon>
                            </div>
                        </div>
                    </template>
                </el-table-column>
                <el-table-column label="操作" width="150" align="center" fixed="right">
                    <template #default="scope">
                        <div class="action-buttons">
                            <el-tooltip content="查看详情" placement="top">
                                <el-button type="primary" :icon="View" circle size="small"
                                    @click="showDetail(scope.row)" />
                            </el-tooltip>
                            <el-tooltip content="删除记录" placement="top">
                                <el-button v-if="canDelete()" type="danger" :icon="Delete" circle size="small"
                                    @click="handleDelete(scope.row.id)" />
                            </el-tooltip>
                        </div>
                    </template>
                </el-table-column>
            </el-table>

            <!-- 分页 -->
            <div class="pagination">
                <el-pagination v-model:current-page="pagination.current" v-model:page-size="pagination.size"
                    :page-sizes="[10, 20, 50, 100]" layout="total, sizes, prev, pager, next, jumper"
                    :total="pagination.total" @size-change="handleSizeChange" @current-change="handleCurrentChange" />
            </div>
        </el-card>

        <!-- 详情弹窗 -->
        <el-dialog v-model="detailVisible" title="违规记录详情" width="700px" top="5vh">
            <el-descriptions :column="1" border>
                <el-descriptions-item label="ID">{{ currentItem.id }}</el-descriptions-item>
                <el-descriptions-item label="UID">{{ currentItem.uid }}</el-descriptions-item>
                <el-descriptions-item label="昵称">{{ currentItem.name }}</el-descriptions-item>
                <el-descriptions-item label="违规行为">{{ currentItem.violation }}</el-descriptions-item>
                <el-descriptions-item label="处理方式">{{ currentItem.handle }}</el-descriptions-item>
                <el-descriptions-item label="处理人">{{ currentItem.handleman }}</el-descriptions-item>
                <el-descriptions-item label="所属平台">
                    <el-tag :type="currentItem.platform === 'B站' ? 'danger' : 'primary'">
                        {{ currentItem.platform }}
                    </el-tag>
                </el-descriptions-item>
                <el-descriptions-item label="图片材料">
                    <div class="detail-image-container">
                        <el-image v-if="currentItem.materials" :src="getImageUrl(currentItem.materials)" fit="contain"
                            style="max-height: 300px;" :preview-src-list="[getImageUrl(currentItem.materials)]" />
                        <span v-else>无图片材料</span>
                    </div>
                </el-descriptions-item>
            </el-descriptions>
            <template #footer>
                <el-button type="primary" @click="detailVisible = false">关闭</el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { canDelete } from '../../../util/permission'
import {
    Search, Refresh, Delete, View, Picture
} from '@element-plus/icons-vue';
import axios from 'axios';
import { ElMessage, ElMessageBox } from 'element-plus';
import { ASSET_BASE } from '../../../util/assetBase';

// 数据相关
const tableData = ref([]);
const filteredData = ref([]);          // 搜索过滤后的完整数据
const loading = ref(false);
const searchQuery = ref('');
const currentItem = reactive({});
const detailVisible = ref(false);

// 分页相关
const pagination = reactive({
    current: 1,
    size: 10,
    total: 0
});

// 表格实际展示的数据：按当前页码/每页条数切片
const filteredTableData = computed(() => {
    const start = (pagination.current - 1) * pagination.size;
    return filteredData.value.slice(start, start + pagination.size);
});

// 获取图片完整URL
const getImageUrl = (path) => {
    if (!path) return '';
    return path.startsWith('http') ? path : `${ASSET_BASE}${path}`;
};

// 获取黑名单列表
const fetchBlacklist = async () => {
    loading.value = true;
    try {
        const response = await axios.get('/adminapi/blackmsg/list');
        // 注意：走到 else 就说明 response.data 是假值，不能再读它的 .message（会抛 TypeError）
        if (response.data) {
            tableData.value = response.data.results || [];
            filteredData.value = tableData.value;
            pagination.current = 1;
            pagination.total = tableData.value.length;
        } else {
            ElMessage.error('获取数据失败');
        }
    } catch (error) {
        console.error('获取黑名单列表失败:', error);
        ElMessage.error('网络异常，请重试');
    } finally {
        loading.value = false;
    }
};

// 搜索处理
const handleSearch = () => {
    if (!searchQuery.value.trim()) {
        filteredData.value = tableData.value;
        pagination.current = 1;
        pagination.total = filteredData.value.length;
        return;
    }

    const query = searchQuery.value.toLowerCase();
    filteredData.value = tableData.value.filter(item =>
        String(item.uid).includes(query) ||
        String(item.name || '').toLowerCase().includes(query)
    );
    pagination.current = 1;
    pagination.total = filteredData.value.length;
};

// 分页处理
const handleSizeChange = (val) => {
    pagination.size = val;
    pagination.current = 1;
};

const handleCurrentChange = (val) => {
    pagination.current = val;
};

// 删除操作
const handleDelete = async (id) => {
    // console.log(id)
    try {
        await ElMessageBox.confirm(
            '此操作将永久删除该记录, 是否继续?',
            '警告',
            {
                confirmButtonText: '确认删除',
                cancelButtonText: '取消',
                type: 'warning',
                center: true
            }
        );

        // 删除走 DELETE（后端路由已从 GET 改过来）
        const response = await axios.delete(`/adminapi/black/${id}`);
        if (response.data.code === 200) {
            ElMessage.success('删除成功');
            fetchBlacklist();
        } else {
            ElMessage.error(response.data.message || '删除失败');
        }
    } catch (error) {
        if (error !== 'cancel') {
            console.error('删除失败:', error);
            ElMessage.error('删除失败，请重试');
        }
    }
};

// 查看详情
const showDetail = (item) => {
    Object.assign(currentItem, item);
    detailVisible.value = true;
};

// 返回上一页
const goBack = () => {
    // 这里可以根据实际路由调整
    window.history.back();
};

// 初始化加载数据
onMounted(() => {
    fetchBlacklist();
});
</script>

<style scoped lang="scss">
.blacklist-container {
    padding: 20px;
    background-color: #f8fafc;
    min-height: calc(100vh - 60px);
}

.header-card {
    margin-bottom: 20px;
    border-radius: 8px;

    .header-content {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .header-title {
        .el-page-header {
            :deep(.el-page-header__content) {
                font-size: 18px;
                font-weight: 600;
                color: #303133;
            }
        }
    }

    .header-actions {
        display: flex;
        gap: 12px;
    }
}

.table-card {
    border-radius: 8px;

    .image-container {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 100%;

        .table-image {
            width: 80px;
            height: 80px;
            border-radius: 4px;
            cursor: pointer;
            transition: transform 0.3s ease;

            &:hover {
                transform: scale(1.05);
            }
        }

        .image-error,
        .no-image {
            width: 80px;
            height: 80px;
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: #f5f7fa;
            border-radius: 4px;
            color: #c0c4cc;

            .el-icon {
                font-size: 24px;
            }
        }
    }

    .action-buttons {
        display: flex;
        gap: 8px;
        justify-content: center;

        .el-button {
            transition: all 0.3s ease;

            &:hover {
                transform: scale(1.1);
            }
        }
    }
}

.pagination {
    margin-top: 20px;
    display: flex;
    justify-content: flex-end;
}

.detail-image-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100px;
}

/* 响应式调整 */
@media (max-width: 768px) {
    .header-content {
        flex-direction: column;
        gap: 12px;
        align-items: flex-start !important;
    }

    .header-actions {
        width: 100%;
        flex-wrap: wrap;
    }

    .action-buttons {
        flex-wrap: wrap;
    }
}
</style>